import React from 'react'
import { connect } from 'react-redux'
import { t } from 'ttag'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { useTitleChange } from '../../util/hooks'
import * as h2Api from '../../h2Api'

const NoLinkButton = styled.button`
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;

  &:defined {
    border-bottom: 1px dotted #ccc;
  }
`
const OccupationComparison = props => {
  //const query2 = useQuery()
  const { comparisonResponse, error, fluidContainer = false } = props

  // console.log(props, query2)
  useTitleChange(t`Occupation Comparison`)
  // useInitDispatch(compareOccupations(query.onetSoc), [query.onetSoc])

  if (!comparisonResponse || !h2Api.receivedResponse(comparisonResponse)) {
    return null
  }

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-warning" role="alert">
          <h3>{error}</h3>
        </div>
      </div>
    )
  }

  const { comparisons, strictRegion, wageRegion, projectionRegion } =
    comparisonResponse?.result
  const makeThreeDigitCode = lst => {
    return lst.map(t => t.name.charAt(0)).join('')
  }

  return (
    <div className={fluidContainer ? 'container-fluid' : 'container'}>
      <table id="comparison" className="tbl">
        <thead>
          <tr>
            <th className="comparison-heading">
              <span className="sr-only">{t`Comparison Attribute`}</span>
            </th>
            {(comparisons || []).map((occ, idx) => (
              <th key={`title${idx}`} className="occupation-title">
                <Link
                  to={occ?.url}
                  style={{ fontWeight: 'bold' }}
                  className="occupation-link regular-match">
                  {occ?.title}
                </Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="odd">
            <th scope="row" className="comparison-heading comparison-jz">
              {t`Job Zone`}
            </th>
            {(comparisons || []).map((comp, idx) => (
              <td className="job-zone" key={`jz${idx}`}>
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="right"
                  overlay={(
                    <Popover
                      title={comp?.jobZone?.name ?? t`Not specified`}
                      style={{ zIndex: '10000000' }}>
                      {comp?.jobZone?.education}
                    </Popover>
                  )}>
                  <NoLinkButton
                    title={comp?.jobZone?.education}
                    className="defined"
                    tabIndex="0">
                    {comp?.jobZone?.name ?? t`Not specified`}
                  </NoLinkButton>
                </OverlayTrigger>
              </td>
            ))}
          </tr>
          <tr className="even">
            <th scope="row" className="comparison-heading comparison-jz">{t`Cluster`}</th>
            {(comparisons || []).map(r => (
              <td className="cluster" key={`cluster-${r?.cluster?.id}`}>
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="right"
                  overlay={(
                    <Popover
                      title={r?.cluster?.name ?? t`Not specified`}
                      style={{ zIndex: '10000000' }}>
                      {r?.cluster?.description}
                    </Popover>
                  )}>
                  <Link
                    href={r?.cluster?.url}
                    tabIndex="0"
                    title={r?.cluster?.name}
                    className="defined">
                    {r?.cluster?.name ?? t`Not specified`}
                  </Link>
                </OverlayTrigger>
              </td>
            ))}
          </tr>
          <tr className="odd">
            <th scope="row" className="comparison-heading comparison-interest">
              {t`Interests`}
            </th>
            {(comparisons || []).map((r, idx) => (
              <td className="interests" key={`inter-${idx}`}>
                <b className="text-center">{makeThreeDigitCode(r?.interests)}</b>
                {r?.interests?.map(i => (
                  <div className="interest">
                    <OverlayTrigger
                      trigger={['hover', 'focus']}
                      placement="right"
                      overlay={(
                        <Popover title={i?.name} style={{ zIndex: '10000000' }}>
                          {i?.description}
                        </Popover>
                      )}>
                      <NoLinkButton tabIndex="0" className="defined">
                        {i?.name}
                      </NoLinkButton>
                    </OverlayTrigger>
                  </div>
                ))}
                {r.hasOwnProperty('interestProfilerStatus') ? (
                  <div className="inassessment">{r?.interestProfilerStatus}</div>
                ) : null}
              </td>
            ))}
          </tr>
          <tr className="even">
            <th scope="row" className="comparison-heading comparison-interest">
              {t`Work Values`}
            </th>
            {(comparisons || []).map((r, idx) => (
              <td className="workValues" key={`wv${idx}`}>
                <b className="text-center">{makeThreeDigitCode(r?.workValues)}</b>
                {r?.workValues?.map(i => (
                  <div className="workValue">
                    <OverlayTrigger
                      trigger={['hover', 'focus']}
                      placement="right"
                      overlay={(
                        <Popover title={i?.name} style={{ zIndex: '10000000' }}>
                          {i?.description}
                        </Popover>
                      )}>
                      <NoLinkButton tabIndex="0" className="defined">
                        {i?.name}
                      </NoLinkButton>
                    </OverlayTrigger>
                  </div>
                ))}
                {r.hasOwnProperty('workImportanceProfilerStatus') ? (
                  <div className="inassessment">{r?.workImportanceProfilerStatus}</div>
                ) : null}
              </td>
            ))}
          </tr>
          <tr className="odd">
            <th scope="row" className="comparison-heading comparison-salary">
              {t`Salary`}
            </th>
            {(comparisons || []).map((w, idx) => (
              <td className="salary" key={`sal-${idx}`}>
                {(() => {
                  try {
                    return w.wages.average.toLocaleString('en', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0
                    })
                  } catch (e) {
                    return 'n/a'
                  }
                })()}
                {(w?.wages && w?.wages?.inBudget) || false ? (
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="right"
                    overlay={(
                      <Popover
                        style={{
                          zIndex: '10000000'
                        }}>
                        {t`This occupation would satisfy the salary requirements from your budget.`}
                      </Popover>
                    )}>
                    <span className="badge listing-badge budget defined">
                      <span>{t`budget`}</span>
                    </span>
                  </OverlayTrigger>
                ) : null}
              </td>
            ))}
          </tr>
          <tr className="even">
            <th scope="row" className="comparison-heading comparison-openings">
              {t`Projected Employment`}
            </th>
            {(comparisons || []).map((o, idx) => (
              <td className="openings" key={`opening-${idx}`}>
                {(() => {
                  try {
                    return o.outlook.projected.toLocaleString()
                  } catch (e) {
                    return 'n/a'
                  }
                })()}
              </td>
            ))}
          </tr>
          <tr className="odd">
            <th scope="row" className="comparison-heading comparison-salary">
              {t`Possible College Majors`}
            </th>
            {(comparisons || []).map((o, idx) => (
              <td className="majors vatop" key={`major${idx}`}>
                <ul className="fa-ul">
                  {(o.cipCodes || []).map(cip => (
                    <li className="major" key={`major${idx}-${cip.url}`}>
                      <i aria-hidden="true" className="fa-li fal fa-graduation-cap"></i>
                      <Link to={cip?.url}>{cip?.title}</Link>
                    </li>
                  ))}
                </ul>
              </td>
            ))}
          </tr>
        </tbody>
        <tfoot>
          <tr className="even">
            <td></td>
            {(comparisons || []).map((r, idx) => (
              <td className="profile-link" key={`comp${idx}`}>
                <Link to={r?.url} className="submit">
                  {t`View Occupation`}
                </Link>
              </td>
            ))}
          </tr>
        </tfoot>
      </table>

      {strictRegion ? null : (
        <div className="disclaimers">
          <div className="region-used">{t`${wageRegion} salary and ${projectionRegion} projected opening information is used in the above comparison`}</div>
        </div>
      )}
    </div>
  )
}

export default connect(
  state => {
    return {
      comparisonResponse: state.occupations.comparisonResponse,
      error: state.occupations.comparison.error
    }
  },
  dispatch => ({})
)(OccupationComparison)
